import { useState, useRef } from 'react'
import styled from 'styled-components'

import DropboxButton from './cloud/Dropbox'
import GoogleButton from './cloud/Google'
import Description from './ContentDescription'
import Title from './ContentTitle'
import ContentWrapper from './ContentWrapper'
import Submit from "./Submit"
import SubmitDescription from "./SubmitDescription"
import { checkUpload, sendFileUpload, sendUrlUpload } from '@templateroller/helpers/dist/api'
import { sendUploadStartEvent, sendUploadFinishEvent } from '@templateroller/helpers/dist/api/dot-events'
import type { TStatus } from '../Uploader'
import { redirectToDraw } from './helpers'
import OnedriveButton from './cloud/Onedrive'
import { DESCRIPTION_UNDER_DOWNLOAD } from './texts'

type possibleServices = 'device'|'dropbox'|'onedrive'|'google'

const DropZone = styled.div<{
    readonly service: string,
    readonly over: boolean,
}>`
    display: flex;
    flex-direction: column;
    align-items: center;

    box-sizing: border-box;
    border-radius: 8px;
    width: 100%;

    background: ${props => props.over ? 'rgba(82, 199, 221, 0.05)' : 'none'};

    * {
        pointer-events: ${props => props.over ? 'none' : 'auto'}
    }

    @media (min-width: 961px) {
        border: ${props => props.service === 'device' ? '2px dashed #52C7DD' : '2px solid #52C7DD'};
        // width: 610px;
    }
`
DropZone.displayName = 'DropZone'

const DropZoneScreen = styled.div<{
    readonly service: string,
    readonly over: boolean,
}>`
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 0;
    `
DropZoneScreen.displayName = 'DropZoneScreen'

const Footer = styled.div`
    display: flex;
    justify-content: space-around;

    flex-wrap: wrap;

    margin-top: 26px;

    @media (min-width: 320px) {
        width: 268px;
        justify-content: space-between;
    }

    @media (min-width: 480px) {
        // flex-wrap: nowrap;
        width: 320px;
    }

    @media (min-width: 640px) {
        width: 328px;
    }

    @media (min-width: 960px) {
        width: 352px;
    }
`
Footer.displayName = 'Upload-Services'

type Props = {
    setUploadStatus: (status: TStatus, message: string, progress: number) => void,
    uploadStatus: string,
}

const Upload = ({
    setUploadStatus,
    uploadStatus,
}: Props) => {
    const [over, setOver] = useState(false)
    const [hasError, setError] = useState(false)
    const ref = useRef<HTMLInputElement>(null)

    const openUpload = () => {
        if (!ref.current) throw Error("divRef is not assigned");
        ref.current.click()
    }

    const successCallback = (docId: number) => redirectToDraw(docId)
    const errorCallback = () => setUploadStatus('error', 'error', 0)

    const startFileUpload = (service: possibleServices, file: File | string, googleFileId?: string, googleToken?: string) => {
        if (typeof file === 'object' && !(file.name.match(/\.pdf$/) && (file.size / 1024 / 1024) < 25)) {
            setError(true)
            return setTimeout(() => {
                setError(false)
                setOver(false)
            }, 3000)
        }

        setUploadStatus('waiting', '', 0)

        sendUploadStartEvent(service).then(
            () => {
                if (typeof file === 'object') {
                    return sendFileUpload(
                        file,
                        setUploadStatus,
                    )
                } else {
                    return sendUrlUpload(
                        service,
                        file,
                        setUploadStatus,
                        googleFileId,
                        googleToken,
                    )
                }
            }
        ).then(async (data) => {
            if (data.status === 'success') {
                let check, errorCount = 0
                while (check === undefined || check.status === 'waiting' || check.status === 'pending') {
                    check = await checkUpload(data.doc_id)

                    if (check.status === 'pending') {
                        setUploadStatus('converting', '', (check.cur_page * 100) / check.total_pages)
                    } else if (check.status === 'waiting') {
                        setUploadStatus('converting', '', 0)
                    } else if (check.status === 'success') {
                        setUploadStatus('redirecting', String(check.doc_id), 0)
                    } else if (check.status === 'error') {
                        errorCount++
                        setUploadStatus(check.status, '', 0)
                    } else {
                        setUploadStatus(check.status, '', 0)
                    }

                    await new Promise(r => setTimeout(r, 3000))
                }

                if (errorCount === 0) {
                    await sendUploadFinishEvent(service, data.doc_id)
                    if (process.env.REACT_APP_ENV === 'production') successCallback(data.doc_id)
                }
            } else {
                errorCallback()
            }
        }).catch(errorCallback)
    }

    if (uploadStatus !== 'ready') {
        return null
    }

    return <ContentWrapper>
        <DropZoneScreen
            service={'device'}
            over={over}
            onDrop={(e) => {
                e.preventDefault()
                const file = e.dataTransfer.items[0].getAsFile()
                if (file !== null) {
                    startFileUpload('device', file)
                }
            }}
            onDragOver={(e) => {
                e.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => {
                setOver(false)
            }}
        >
        </DropZoneScreen>
        <DropZone
            service={'device'}
            over={over}
            onDrop={(e) => {
                e.preventDefault()
                const file = e.dataTransfer.items[0].getAsFile()
                if (file !== null) {
                    startFileUpload('device', file)
                }
            }}
            onDragOver={(e) => {
                e.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => {
                setOver(false)
            }}
        >
            <ContentWrapper>
                <Title>Drag and Drop Document Here to Get Started</Title>
                <Description>Use the button below to upload your document and begin editing</Description>
                <Submit onClick={() => openUpload()}>Browse for a document</Submit>
                <SubmitDescription error={hasError}>{DESCRIPTION_UNDER_DOWNLOAD}</SubmitDescription>
            </ContentWrapper>

        </DropZone>

        <Footer>
            <DropboxButton callback={(data) => {
                startFileUpload('dropbox', data)
            }} />

            <OnedriveButton callback={(data) => {
                startFileUpload('onedrive', data)
            }} />

            <GoogleButton callback={(data) => {
                startFileUpload('google', data.url, data.id, data.token)
            }} />
        </Footer>        

        <input
            type="file"
            ref={ref}
            style={{display: 'none'}}
            onChange={(e) => {
                const files = e.target.files
                if (files !== null) {
                    startFileUpload('device', files[0])
                }
            }}
        />
    </ContentWrapper>
}

export default Upload